import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { Quotation } from '../data/dummyData';

interface AdvancedSearchProps {
  quotations: Quotation[];
  onSearchResults: (results: Quotation[]) => void;
  onClose: () => void;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({ quotations, onSearchResults, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    channel: '',
    priority: '',
    serviceType: '',
    assignedTo: '',
    dateRange: '',
    minValue: '',
    maxValue: '',
    origin: '',
    destination: ''
  });

  const [savedSearches] = useState([
    { name: 'High Priority Pending', query: 'priority:High status:New,Acknowledged' },
    { name: 'Large Value Opportunities', query: 'estimatedValue:>10000' },
    { name: 'This Week\'s Quotes', query: 'dateRange:7d' },
    { name: 'Email Channel', query: 'channel:Email' }
  ]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const performSearch = () => {
    let results = [...quotations];

    // Text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      results = results.filter(q => 
        q.subject.toLowerCase().includes(term) ||
        q.customerName.toLowerCase().includes(term) ||
        q.company.toLowerCase().includes(term) ||
        q.cargoDescription.toLowerCase().includes(term) ||
        q.specialRequirements.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.status) {
      results = results.filter(q => q.status === filters.status);
    }
    if (filters.channel) {
      results = results.filter(q => q.channel === filters.channel);
    }
    if (filters.priority) {
      results = results.filter(q => q.priority === filters.priority);
    }
    if (filters.serviceType) {
      results = results.filter(q => q.serviceType === filters.serviceType);
    }
    if (filters.assignedTo) {
      results = results.filter(q => q.assignedTo === filters.assignedTo);
    }
    if (filters.origin) {
      results = results.filter(q => q.origin.toLowerCase().includes(filters.origin.toLowerCase()));
    }
    if (filters.destination) {
      results = results.filter(q => q.destination.toLowerCase().includes(filters.destination.toLowerCase()));
    }
    if (filters.minValue) {
      results = results.filter(q => (q.estimatedValue || 0) >= parseFloat(filters.minValue));
    }
    if (filters.maxValue) {
      results = results.filter(q => (q.estimatedValue || 0) <= parseFloat(filters.maxValue));
    }

    // Date range filter
    if (filters.dateRange) {
      const now = new Date();
      const days = filters.dateRange === '7d' ? 7 : filters.dateRange === '30d' ? 30 : 90;
      const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      results = results.filter(q => new Date(q.received) >= cutoffDate);
    }

    onSearchResults(results);
    onClose();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      status: '',
      channel: '',
      priority: '',
      serviceType: '',
      assignedTo: '',
      dateRange: '',
      minValue: '',
      maxValue: '',
      origin: '',
      destination: ''
    });
  };

  const loadSavedSearch = (query: string) => {
    // Parse saved search query (simplified implementation)
    if (query.includes('priority:High')) {
      handleFilterChange('priority', 'High');
    }
    if (query.includes('status:New,Acknowledged')) {
      handleFilterChange('status', 'New');
    }
    if (query.includes('channel:Email')) {
      handleFilterChange('channel', 'Email');
    }
    if (query.includes('dateRange:7d')) {
      handleFilterChange('dateRange', '7d');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Advanced Search</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>

          {/* Search Input */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search quotations, customers, companies, or descriptions..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Saved Searches */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Saved Searches</h3>
            <div className="flex flex-wrap gap-2">
              {savedSearches.map((search, index) => (
                <button
                  key={index}
                  onClick={() => loadSavedSearch(search.query)}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-700 transition-colors"
                >
                  {search.name}
                </button>
              ))}
            </div>
          </div>

          {/* Filters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Statuses</option>
                <option value="New">New</option>
                <option value="Acknowledged">Acknowledged</option>
                <option value="Quoted">Quoted</option>
                <option value="Won">Won</option>
                <option value="Lost">Lost</option>
              </select>
            </div>

            {/* Channel Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
              <select
                value={filters.channel}
                onChange={(e) => handleFilterChange('channel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Channels</option>
                <option value="Email">Email</option>
                <option value="WhatsApp">WhatsApp</option>
                <option value="Phone">Phone</option>
                <option value="Web">Web</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Priorities</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            {/* Service Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
              <select
                value={filters.serviceType}
                onChange={(e) => handleFilterChange('serviceType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Services</option>
                <option value="Air Freight">Air Freight</option>
                <option value="Ocean Freight">Ocean Freight</option>
                <option value="Express Delivery">Express Delivery</option>
                <option value="Road Transport">Road Transport</option>
              </select>
            </div>

            {/* Assigned To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
              <select
                value={filters.assignedTo}
                onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Assignees</option>
                <option value="John Smith">John Smith</option>
                <option value="Sarah Johnson">Sarah Johnson</option>
                <option value="Mike Chen">Mike Chen</option>
                <option value="Lisa Brown">Lisa Brown</option>
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Time</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
            </div>

            {/* Value Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Value ($)</label>
              <input
                type="number"
                value={filters.minValue}
                onChange={(e) => handleFilterChange('minValue', e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Value ($)</label>
              <input
                type="number"
                value={filters.maxValue}
                onChange={(e) => handleFilterChange('maxValue', e.target.value)}
                placeholder="100000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Location Filters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Origin</label>
              <input
                type="text"
                value={filters.origin}
                onChange={(e) => handleFilterChange('origin', e.target.value)}
                placeholder="e.g., Mumbai, Delhi"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Destination</label>
              <input
                type="text"
                value={filters.destination}
                onChange={(e) => handleFilterChange('destination', e.target.value)}
                placeholder="e.g., Dubai, Singapore"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Clear All Filters
            </button>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={performSearch}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
              >
                <Search className="w-4 h-4" />
                <span>Search</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearch;
